import { Component, OnInit, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { loadStripe, Stripe, StripeCardElement, StripeElements } from '@stripe/stripe-js';
import { ConsultationService } from '../../shared/services/consultation.service';
import { IConsultationSlot, PaymentProvider } from '../../../assets/types/dtoTypes';
import { environment } from '../../environments/environment';
import { CountryService } from "../../shared/services/country.service";
import { CountryCode, PRICING } from "../../pages/registration-pages/pricing-page/pricing.config";

interface CalendarDay {
  date: Date;
  inMonth: boolean;
  isPast: boolean;
}

@Component({
  selector: 'app-book-consultation-modal',
  templateUrl: './book-consultation-modal.component.html',
  styleUrls: ['./book-consultation-modal.component.scss'],
  standalone: true,
  imports: [NgFor, NgIf, FormsModule, MatIcon, TranslateModule],
})
export class BookConsultationModalComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<BookConsultationModalComponent>);
  private consultationService = inject(ConsultationService);

  private readonly countryService = inject(CountryService);

  country: CountryCode = 'EU';

  readonly weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  step: 1 | 2 = 1;

  viewDate = new Date();
  selectedDate: Date | null = null;
  selectedSlot: IConsultationSlot | null = null;

  allSlots: IConsultationSlot[] = [];

  paymentMethod: 'CARD' | 'BANK_TRANSFER' = 'CARD';

  paymentLoading = false;
  submitting = false;
  paymentError: string | null = null;
  bookingId: string | null = null;

  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;
  private cardElement: StripeCardElement | null = null;
  private clientSecret: string | null = null;

  ngOnInit(): void {
    this.countryService.getCountry().subscribe(country => {
      this.country = country;
    });

    this.loadSlots();
  }

  get consultationPrice(): number {
    return PRICING.basic[this.country].price;
  }

  get consultationCurrency(): string {
    return PRICING.basic[this.country].currency;
  }

  get formattedConsultationPrice(): string {
    const locale =
      this.country === 'TN'
        ? 'fr-TN'
        : this.country === 'MA'
          ? 'fr-MA'
          : this.country === 'IN'
            ? 'en-IN'
            : 'de-DE';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: this.consultationCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(this.consultationPrice);
  }

  loadSlots(): void {
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();
    const from = new Date(year, month, 1);
    const to = new Date(year, month + 1, 0, 23, 59, 59);
    this.consultationService.getSlots(from, to).subscribe({
      next: (slots) => (this.allSlots = slots.filter((s) => s.available)),
      error: () => (this.allSlots = []),
    });
  }

  get canGoBack(): boolean {
    const today = new Date();
    return (
      this.viewDate.getFullYear() > today.getFullYear() ||
      this.viewDate.getMonth() > today.getMonth()
    );
  }

  get canContinue(): boolean {
    return this.selectedSlot !== null && !this.paymentLoading;
  }

  get monthLabel(): string {
    return this.viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  get timezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  get formattedDate(): string {
    if (!this.selectedSlot) return '';
    return new Date(this.selectedSlot.startAt).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
    });
  }

  get formattedTime(): string {
    if (!this.selectedSlot) return '';
    return this.formatSlotTime(this.selectedSlot);
  }

  get morningSlots(): IConsultationSlot[] {
    return this.slotsForSelectedDate.filter((s) => new Date(s.startAt).getHours() < 12);
  }

  get afternoonSlots(): IConsultationSlot[] {
    return this.slotsForSelectedDate.filter((s) => new Date(s.startAt).getHours() >= 12);
  }

  private get slotsForSelectedDate(): IConsultationSlot[] {
    if (!this.selectedDate) return [];
    const key = this.dateKey(this.selectedDate);
    return this.allSlots
      .filter((s) => this.dateKey(new Date(s.startAt)) === key)
      .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
  }

  private get availableDateKeys(): Set<string> {
    const set = new Set<string>();
    for (const s of this.allSlots) set.add(this.dateKey(new Date(s.startAt)));
    return set;
  }

  hasSlots(day: CalendarDay): boolean {
    return this.availableDateKeys.has(this.dateKey(day.date));
  }

  formatSlotTime(slot: IConsultationSlot): string {
    return new Date(slot.startAt).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  private dateKey(d: Date): string {
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }

  get calendarDays(): CalendarDay[] {
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let startOffset = firstDay.getDay() - 1;
    if (startOffset < 0) startOffset = 6;

    const days: CalendarDay[] = [];

    for (let i = startOffset - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, inMonth: false, isPast: d < today });
    }

    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      days.push({ date, inMonth: true, isPast: date < today });
    }

    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let d = 1; d <= remaining; d++) {
        const date = new Date(year, month + 1, d);
        days.push({ date, inMonth: false, isPast: false });
      }
    }

    return days;
  }

  prevMonth(): void {
    if (!this.canGoBack) return;
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);
    this.selectedDate = null;
    this.selectedSlot = null;
    this.loadSlots();
  }

  nextMonth(): void {
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1);
    this.selectedDate = null;
    this.selectedSlot = null;
    this.loadSlots();
  }

  selectDate(day: CalendarDay): void {
    if (!day.inMonth || day.isPast) return;
    this.selectedDate = day.date;
    this.selectedSlot = null;
  }

  isSelected(day: CalendarDay): boolean {
    if (!this.selectedDate || !day.date) return false;
    return (
      day.date.getFullYear() === this.selectedDate.getFullYear() &&
      day.date.getMonth() === this.selectedDate.getMonth() &&
      day.date.getDate() === this.selectedDate.getDate()
    );
  }

  selectSlot(slot: IConsultationSlot): void {
    this.selectedSlot = slot;
  }

  isSlotSelected(slot: IConsultationSlot): boolean {
    return this.selectedSlot?.id === slot.id;
  }

  // calendarDays / morning- / afternoonSlots are getters that return a fresh array on every
  // change-detection pass. Without trackBy, Angular tears down and rebuilds these elements on
  // each CD cycle — and on first login the constant async churn (websocket, notifications) can
  // recreate a day between mousedown and click, so the click is lost. Stable keys fix that.
  trackByDay = (_: number, day: CalendarDay): number => day.date.getTime();

  trackBySlot = (_: number, slot: IConsultationSlot): string => slot.id;

  goToPayment(): void {
    if (!this.canContinue || !this.selectedSlot) return;
    this.paymentError = null;
    this.paymentLoading = true;

    if (this.paymentMethod === 'BANK_TRANSFER') {
      this.paymentLoading = false;
      this.step = 2;
      return;
    }

    this.consultationService
      .createBooking({ slotId: this.selectedSlot.id, provider: PaymentProvider.STRIPE, country: this.country, })
      .subscribe({
        next: async (booking) => {
          this.bookingId = booking.id;
          this.clientSecret = booking.clientSecret ?? null;
          this.paymentLoading = false;
          this.step = 2;
          await this.mountPaymentElement();
        },
        error: (err) => {
          this.paymentLoading = false;
          this.paymentError = err?.error?.message || 'Failed to start booking. Please try again.';
        },
      });
  }

  confirmBankTransfer(): void {
    if (!this.selectedSlot || this.submitting) return;
    this.submitting = true;
    this.paymentError = null;

    this.consultationService
      .createBooking({ slotId: this.selectedSlot.id, provider: PaymentProvider.BANK_TRANSFER, country: this.country })
      .subscribe({
        next: (booking) => {
          this.submitting = false;
          this.dialogRef.close({ success: true, bookingId: booking.id });
        },
        error: (err) => {
          this.submitting = false;
          this.paymentError = err?.error?.message || 'Failed to confirm booking. Please try again.';
        },
      });
  }

  private async mountPaymentElement(): Promise<void> {
    if (!this.clientSecret) return;
    if (!this.stripe) {
      this.stripe = await loadStripe(environment.stripePublishableKey);
    }
    if (!this.stripe) {
      this.paymentError = 'Payment provider failed to load.';
      return;
    }
    this.elements = this.stripe.elements({ clientSecret: this.clientSecret });
    this.cardElement = this.elements.create('card', {
      style: {
        base: {
          fontSize: '15px',
          color: '#0f172a',
          fontFamily: 'inherit',
          '::placeholder': { color: '#94a3b8' },
        },
        invalid: { color: '#dc2626' },
      },
    });
    await this.waitForElement('#payment-element');
    this.cardElement.mount('#payment-element');
  }

  private waitForElement(selector: string, timeoutMs = 2000): Promise<HTMLElement> {
    return new Promise((resolve, reject) => {
      const found = document.querySelector<HTMLElement>(selector);
      if (found) return resolve(found);
      const start = Date.now();
      const observer = new MutationObserver(() => {
        const el = document.querySelector<HTMLElement>(selector);
        if (el) {
          observer.disconnect();
          resolve(el);
        } else if (Date.now() - start > timeoutMs) {
          observer.disconnect();
          reject(new Error(`Timed out waiting for ${selector}`));
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  goBack(): void {
    this.step = 1;
    this.cardElement?.unmount();
    this.cardElement = null;
    this.elements = null;
    this.clientSecret = null;
    this.bookingId = null;
    this.paymentError = null;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  async bookNow(): Promise<void> {
    if (!this.stripe || !this.cardElement || !this.clientSecret || this.submitting) return;
    this.submitting = true;
    this.paymentError = null;

    const result = await this.stripe.confirmCardPayment(this.clientSecret, {
      payment_method: { card: this.cardElement },
    });

    this.submitting = false;

    if (result.error) {
      this.paymentError = result.error.message || 'Payment failed.';
      return;
    }
    if (result.paymentIntent?.status === 'succeeded') {
      this.dialogRef.close({ success: true, bookingId: this.bookingId });
    } else {
      this.paymentError = `Payment status: ${result.paymentIntent?.status}`;
    }
  }
}
